import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenedStore } from '../db/context';
import {
  dataDirInfo,
  makeBackup,
  pickBackup,
  pickFolder,
  restoreBackup,
  setDataDir,
  type DataDirInfo,
} from '../db/backup';
import { ConfirmDialog } from './Dialog';
import { exportToZip } from '../lib/export';
import { useUi } from '../state/ui';

const button =
  'justify-self-start rounded-full border border-border px-3 py-1 text-sm hover:bg-bg-hover disabled:opacity-50';

/** Settings → About: where the data lives, backup and restore. */
export function DataSettings() {
  const { t } = useTranslation();
  const opened = useOpenedStore();
  const desktop = opened.backend === 'desktop';
  const [info, setInfo] = useState<DataDirInfo>();
  const [status, setStatus] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [restoreFrom, setRestoreFrom] = useState<string | Uint8Array | null>(null);
  const [newDir, setNewDir] = useState<string | null>(null);

  useEffect(() => {
    if (desktop) void dataDirInfo().then(setInfo, () => {});
  }, [desktop]);

  const run = async (work: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setStatus(undefined);
    try {
      await work();
      if (done) setStatus(done);
    } catch (e) {
      const msg = String(e instanceof Error ? e.message : e);
      setStatus(
        msg === 'invalid'
          ? t('data.invalidBackup')
          : msg === 'exists'
            ? t('data.folderHasData')
            : msg.startsWith('locked:')
              ? t('data.folderLocked', { host: msg.slice(7) })
              : msg,
      );
    } finally {
      setBusy(false);
    }
  };

  const canBackup = desktop || !!opened.driver.exportDb;

  return (
    <div className="grid grid-cols-[8rem_1fr] items-start gap-x-4 gap-y-3 text-sm">
      {desktop && info && (
        <>
          <span className="pt-1">{t('data.folder')}</span>
          <div className="min-w-0 space-y-2">
            <code className="block break-all text-xs text-fg-muted">{info.dir}</code>
            {info.synced && <p className="text-xs text-danger">{t('data.syncedWarning')}</p>}
            {info.fixed ? (
              <p className="text-xs text-fg-muted">{t('data.fixed')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={button}
                  disabled={busy}
                  onClick={() => void pickFolder().then((d) => d && setNewDir(d))}
                >
                  {t('data.change')}
                </button>
                {info.custom && (
                  <button
                    type="button"
                    className={button}
                    disabled={busy}
                    onClick={() => setNewDir(info.defaultDir)}
                  >
                    {t('data.useDefault')}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <span className="pt-1">{t('data.backup')}</span>
      <div className="space-y-2">
        <p className="text-xs text-fg-muted">{t('data.backupHint')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={button}
            disabled={busy || !canBackup}
            onClick={() =>
              void run(async () => {
                if (!(await makeBackup(opened))) throw new Error('');
              }, t('data.backupDone'))
            }
          >
            {t('data.makeBackup')}
          </button>
          <button
            type="button"
            className={button}
            disabled={busy || !canBackup}
            onClick={() => void pickBackup(opened).then((src) => src && setRestoreFrom(src))}
          >
            {t('data.restore')}
          </button>
        </div>
        {status && (
          <p role="status" className="text-xs text-fg-muted">
            {status}
          </p>
        )}
      </div>

      <span className="pt-1">{t('data.markdown')}</span>
      <div className="space-y-2">
        <p className="text-xs text-fg-muted">{t('data.markdownHint')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={button}
            disabled={busy}
            onClick={() => void run(() => exportToZip(opened.store))}
          >
            {t('export.command')}
          </button>
          <button
            type="button"
            className={button}
            onClick={() => {
              useUi.getState().set({ settingsOpen: false });
              useUi.getState().open({ kind: 'nexus-import' });
            }}
          >
            {t('nexusImport.command')}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={restoreFrom !== null}
        onOpenChange={(o) => !o && setRestoreFrom(null)}
        title={t('data.restoreTitle')}
        description={t(desktop ? 'data.restoreBodyDesktop' : 'data.restoreBody')}
        confirmLabel={t('data.restoreConfirm')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => {
          const src = restoreFrom;
          setRestoreFrom(null);
          if (src) void run(() => restoreBackup(opened, src));
        }}
      />

      {newDir && (
        <MoveDialog
          dir={newDir}
          onClose={() => setNewDir(null)}
          onChoose={(mode) => {
            const dir = newDir === info?.defaultDir ? null : newDir;
            setNewDir(null);
            void run(() => setDataDir(dir, mode));
          }}
        />
      )}
    </div>
  );
}

/** Take the data along to the new folder, or open what is there. */
function MoveDialog(props: {
  dir: string;
  onClose: () => void;
  onChoose: (mode: 'move' | 'open') => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmChoice
      title={t('data.changeTitle')}
      body={t('data.changeBody', { dir: props.dir })}
      onClose={props.onClose}
      choices={[
        { label: t('data.move'), onClick: () => props.onChoose('move') },
        { label: t('data.open'), onClick: () => props.onChoose('open') },
      ]}
    />
  );
}

function ConfirmChoice(props: {
  title: string;
  body: string;
  onClose: () => void;
  choices: { label: string; onClick: () => void }[];
}) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onKeyDown={(e) => e.key === 'Escape' && props.onClose()}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-bg p-5 shadow-xl">
        <h2 className="text-lg font-semibold">{props.title}</h2>
        <p className="mt-2 text-sm whitespace-pre-line text-fg-muted">{props.body}</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover"
            onClick={props.onClose}
          >
            {t('app.cancel')}
          </button>
          {props.choices.map((c) => (
            <button
              key={c.label}
              type="button"
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg"
              onClick={c.onClick}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
